import { useEffect, useState } from 'react';
import { formatMoney, TERMS_VERSION, type BillingLink, type MeResponse, type MyBilling } from '@hearth/shared';
import type { Api } from '../api';
import { linkTo } from '../router';
import { errorText } from './serverWords';

// /account/billing (B5, as the canvas board, wording approved 2026-10-27): the payment method monthly
// invoices charge (added on Stripe's Checkout page, changed in Stripe's customer portal), this month so
// far, past invoices (the portal), and a banner while an invoice is unpaid.

/** "Visa ending 4242", "Link · sam@example.com", or the method's type. */
export function methodText(method: NonNullable<MyBilling['paymentMethod']>): string {
  if (method.type === 'card') {
    const brand = method.brand ? method.brand[0]!.toUpperCase() + method.brand.slice(1) : 'Card';
    return method.last4 ? `${brand} ending ${method.last4}` : brand;
  }
  if (method.type === 'link') return method.email ? `Link · ${method.email}` : 'Link';
  return method.type;
}

/** The short mark beside it: VISA, LINK, CARD. */
const chipText = (method: NonNullable<MyBilling['paymentMethod']>) => (method.type === 'card' ? (method.brand ?? 'card') : method.type).slice(0, 4).toUpperCase();

/** "October" (UTC month). */
const monthOnly = (month: string) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' });

/** "1 November": the next month's first day, when this one is billed. */
function billedOn(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y!, m!, 1)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** "created, 0.6 h awake", "0.6 h awake", "created". */
function serverLine(s: MyBilling['thisMonth']['servers'][number]): string {
  const hours = s.seconds > 0 ? `${(s.seconds / 3600).toFixed(1)} h awake` : '';
  return [s.created ? 'created' : '', hours].filter(Boolean).join(', ');
}

/** Opens one of Stripe's pages: Checkout (add a method, back to `returnTo`) or the customer portal. */
export async function openStripe(api: Api, page: 'card' | 'portal', returnTo?: string): Promise<void> {
  const { url } = await api.post<BillingLink>(`/v1/me/billing/${page}`, page === 'card' && returnTo ? { returnTo } : {});
  window.location.assign(url);
}

/**
 * Agrees to the current Terms, once their box is ticked: adding a card and creating a server need it.
 * `over18`: the Terms say only people 18 or older may add a payment method or own a server.
 */
export const acceptTerms = (api: Api) => api.post<void>('/v1/me/terms', { version: TERMS_VERSION, over18: true });

/** The Terms box (the canvas's consent board): unticked until they tick it; the Terms open in a new tab, keeping this page. */
export function TermsCheck({ checked, disabled, onChange, describedBy }: { checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void; describedBy?: string }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-describedby={describedBy} />
      <span>
        I agree to the{' '}
        <a href="/terms" target="_blank" rel="noreferrer">
          Terms
        </a>
        , including prices and refunds.
      </span>
    </label>
  );
}

export function BillingPage({ api, me }: { api: Api; me: MeResponse }) {
  const [billing, setBilling] = useState<MyBilling>();
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Stripe sends the browser back with `?card=saved` once a method was saved.
  const [saved] = useState(() => new URLSearchParams(globalThis.location?.search ?? '').get('card') === 'saved');

  useEffect(() => {
    api.get<MyBilling>('/v1/me/billing').then(setBilling, (err: unknown) => setError(errorText(err)));
  }, [api]);

  const go = (page: 'card' | 'portal') => {
    setBusy(true);
    setError('');
    (async () => {
      if (page === 'card' && !me.termsAccepted) await acceptTerms(api);
      await openStripe(api, page);
    })().catch((err: unknown) => {
      setError(errorText(err));
      setBusy(false);
    });
  };
  return (
    <BillingView
      billing={billing}
      saved={saved}
      busy={busy}
      error={error}
      terms={me.termsAccepted ? undefined : { ticked, onTick: setTicked }}
      onAdd={() => go('card')}
      onPortal={() => go('portal')}
    />
  );
}

/** The page, given what's loaded (no fetching: easy to test). */
export function BillingView({ billing, saved, busy, error, terms, onAdd, onPortal }: {
  billing: MyBilling | undefined;
  saved: boolean;
  busy: boolean;
  error: string;
  /** Not yet agreed to the current Terms: their box, which adding a method needs ticked. */
  terms?: { ticked: boolean; onTick: (ticked: boolean) => void } | undefined;
  onAdd: () => void;
  onPortal: () => void;
}) {
  const method = billing?.paymentMethod ?? null;
  const month = billing?.thisMonth;
  return (
    <div className="billing">
      <a className="back" {...linkTo('/app')}>← Servers</a>
      <div className="page-head">
        <h1>Billing</h1>
        <p className="quiet">You pay for the servers you own: $5 to create one, then by the hour while it's awake. Billed on the 1st of each month.</p>
      </div>
      {error && <p className="notice" role="alert">{error}</p>}
      {billing?.pastDue && (
        <div className="billing-alert billing-alert-due" role="alert">
          <div>
            <strong>You have unpaid invoices</strong>
            <p>Your servers can't wake, and you can't create new ones, until it's paid. Update your payment method, then pay it.</p>
          </div>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={onPortal}>Pay now</button>
        </div>
      )}
      {saved && (
        <p className="billing-alert billing-alert-ok" role="status"><strong>Payment method saved.</strong></p>
      )}
      {!billing ? (
        !error && <p className="quiet" aria-live="polite">Loading…</p>
      ) : (
        <>
          <div className="billing-grid">
            <section className="panel">
              <h2>Payment method</h2>
              {method ? (
                <>
                  <div className="billing-method">
                    <span className="billing-chip" aria-hidden="true">{chipText(method)}</span>
                    <div>
                      <strong>{methodText(method)}</strong>
                      <p className="quiet">Charged on the 1st of each month</p>
                    </div>
                  </div>
                  <div><button className="btn" type="button" disabled={busy} onClick={onPortal}>Change</button></div>
                </>
              ) : (
                <>
                  <p className="quiet">No payment method yet. Add one to create servers.</p>
                  {terms && <TermsCheck checked={terms.ticked} disabled={busy} onChange={terms.onTick} />}
                  <div><button className="btn btn-primary" type="button" disabled={busy || (terms && !terms.ticked)} onClick={onAdd}>Add a payment method</button></div>
                </>
              )}
              <p className="quiet billing-small">Payment details are handled by Stripe. Hearth never sees your card number.</p>
            </section>
            {month && (
              <section className="panel">
                <h2>{`${monthOnly(month.month)} so far`}</h2>
                <span className="billing-total">{formatMoney(month.cents * 100)}</span>
                {month.servers.length > 0 && (
                  <ul className="billing-rows">
                    {month.servers.map((s) => (
                      <li key={s.serverId}>
                        <span>{s.name} <span className="quiet">{`· ${serverLine(s)}`}</span></span>
                        <span>{formatMoney(s.cents * 100)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="quiet">{`Billed on ${billedOn(month.month)}.`}</p>
              </section>
            )}
          </div>
          {(method || billing.pastDue) && (
            <section className="panel billing-invoices">
              <div>
                <h2>Past invoices</h2>
                <p className="quiet">Every invoice and receipt, kept by Stripe.</p>
              </div>
              <button className="btn" type="button" disabled={busy} onClick={onPortal}>View invoices</button>
            </section>
          )}
        </>
      )}
    </div>
  );
}
